import './accept-invite.screen.scss';
import {Typography, Avatar, Box, Button} from '@mui/material';
import {useContext, useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useHistory, useLocation} from 'react-router-dom';
import {PublicContentPage} from '../../../../components/public-content-page/public-content-page';
import {SplashScreen} from '../../../../components/splash-screen/splash-screen';
import {AppContext} from '../../../../contexts/app/context';
import {AppContextType} from '../../../../contexts/app/types';
import {SnackBarContext} from '../../../../contexts/snack-bar/context';
import {SnackBarContextType} from '../../../../contexts/snack-bar/types';
import {ScreenPaths} from '../../../../enums/screen-path';
import {translations} from '../../../../i18n/translations';
import {InvitesRepository} from '../../../../repositories/invites';
import {getBrandName} from '../../../../utils/functions/get-product-name';
import {InviteIdentification} from '../../../../interfaces/invite-identification';
import {InviteIdentificationType} from '../../../../enums/invite-identification-type';
import {InviteType} from '../../../../backend/enums/invite-type';
import {RelationshipInviteData} from '../../../../backend/interfaces/relationship-invite-data';
import {AffiliationType} from '../../../../backend/enums/affiliation-type';
import {AffiliationInviteData} from '../../../../backend/interfaces/affiliation-invite-data';
import {avatarSizesAndCoefficients} from '../../../../utils/constants/avatar-sizes-and-coefficients';
import LoginIcon from '@mui/icons-material/Login';
import PersonAddAlt1Icon from '@mui/icons-material/PersonAddAlt1';
import PersonIcon from '@mui/icons-material/Person';
import BusinessIcon from '@mui/icons-material/Business';

export default function AcceptInviteScreen(props: {type: 'relationship' | 'affiliation'}) {
  // Get context
  const snackBarContext: SnackBarContextType = useContext(SnackBarContext);
  const appContext: AppContextType = useContext(AppContext);

  // Hold state
  const [loadingInviteData, setLoadingInviteData] = useState<boolean>(true);
  const [inviteData, setInviteData] = useState<
    | {
        voucherCode: string;
        targetUserPicture?: string;
        targetUserName?: string;
        targetUserAffiliation?: string;
        targetEntityPicture?: string;
        targetEntityName?: string;
        targetEntityAffiliationType?: AffiliationType;
      }
    | undefined
  >();

  // Translations
  const {t} = useTranslation();

  // Get query parameters and history
  let {search} = useLocation();
  let history = useHistory();

  // Validate the invite
  useEffect(() => {
    // Get invite
    const getInvite = async (code: string) => {
      try {
        // Loading
        setLoadingInviteData(true);

        // Send request
        const response = await InvitesRepository.instance.getInviteByVoucher<
          RelationshipInviteData & AffiliationInviteData
        >(code.trim());

        // Validate the invite
        if (
          (props.type === 'relationship' && response.type !== InviteType.Relationship) ||
          (props.type === 'affiliation' && response.type !== InviteType.Affiliation)
        ) {
          // Error
          throw new Error('Invalid invite type.');
        }

        // Set the invite data
        setInviteData({
          voucherCode: code.trim(),
          targetUserPicture:
            props.type === 'relationship' ? response.data?.targetUser?.picture : undefined,
          targetUserName:
            props.type === 'relationship' ? response.data?.targetUser?.name : undefined,
          targetUserAffiliation:
            props.type === 'relationship'
              ? response.data?.targetUser?.affiliations &&
                Array.isArray(response.data?.targetUser?.affiliations)
                ? response.data.targetUser.affiliations[0].entity.name
                : undefined
              : undefined,
          targetEntityName:
            props.type === 'affiliation' ? response.data?.targetEntity?.name : undefined,
          targetEntityPicture:
            props.type === 'affiliation' ? response.data?.targetEntity?.picture : undefined,
          targetEntityAffiliationType:
            props.type === 'affiliation' ? response.data?.type : undefined,
        });

        // Stop loading
        setLoadingInviteData(false);
      } catch (error) {
        // Error
        snackBarContext.setPublicToastNotification({
          message: t(translations.accept_invite.errors.invalid_invite_error_message),
          severity: 'error',
        });

        // Get out
        history.replace(ScreenPaths.Login);
      }
    };

    // Look for the voucherCode in the path
    if (props.type && search && search.split('?voucherCode=')[1]) {
      // Get the invite
      getInvite(search.split('?voucherCode=')[1]);
    } else {
      // Get out
      history.replace(ScreenPaths.Login);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Navigate to the login screen
  const handleNavigateToLogin = () => {
    history.push({
      pathname: ScreenPaths.Login,
      state: {
        invite: {
          identificationType: InviteIdentificationType.VoucherCode,
          identificationValue: inviteData?.voucherCode as string,
          inviteType:
            props.type === 'relationship' ? InviteType.Relationship : InviteType.Affiliation,
        } as InviteIdentification,
      },
    });
  };

  // Navigate to the create caregiver account screen
  const handleNavigateToCreateAccount = () => {
    history.push({
      pathname:
        props.type === 'relationship'
          ? ScreenPaths.CreateInformalCaregiverAccount
          : ScreenPaths.CreateProfessionalCaregiverAccount,
      search: `?voucherCode=${inviteData?.voucherCode.trim()}`,
    });
  };

  return (
    <>
      {loadingInviteData ? (
        <SplashScreen />
      ) : (
        <PublicContentPage>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 'bold',
            }}
            className="accept-invite-title"
          >
            {props.type === 'relationship'
              ? t(translations.accept_invite.relationship_invite_title)
              : t(translations.accept_invite.affiliation_invite_title)}
          </Typography>
          <Avatar
            alt={
              props.type === 'relationship'
                ? inviteData?.targetUserName
                : inviteData?.targetEntityName
            }
            src={
              props.type === 'relationship'
                ? inviteData?.targetUserPicture
                : inviteData?.targetEntityPicture
            }
            sx={{
              width: `${avatarSizesAndCoefficients.avatarSizes.extraGiant}px`,
              height: `${avatarSizesAndCoefficients.avatarSizes.extraGiant}px`,
              borderRadius:
                props.type === 'relationship'
                  ? `${
                      avatarSizesAndCoefficients.avatarSizes.extraGiant *
                      avatarSizesAndCoefficients.borderRadiusCoefficients.circle
                    }px`
                  : `${
                      avatarSizesAndCoefficients.avatarSizes.extraGiant *
                      avatarSizesAndCoefficients.borderRadiusCoefficients.roundedCornersLow
                    }px`,
              margin: 'auto',
            }}
          >
            {props.type === 'relationship' ? (
              <PersonIcon
                sx={{
                  fontSize:
                    avatarSizesAndCoefficients.avatarSizes.extraGiant *
                    avatarSizesAndCoefficients.iconSizeCoefficient,
                }}
              />
            ) : (
              <BusinessIcon
                sx={{
                  fontSize:
                    avatarSizesAndCoefficients.avatarSizes.extraGiant *
                    avatarSizesAndCoefficients.iconSizeCoefficient,
                }}
              />
            )}
          </Avatar>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 'bold',
            }}
            className="accept-invite-target-name"
          >
            {props.type === 'relationship'
              ? inviteData?.targetUserName
              : inviteData?.targetEntityName}
          </Typography>
          <Typography variant="body1" className="accept-invite-instructions">
            {props.type === 'relationship'
              ? inviteData?.targetUserAffiliation
              : t(translations.accept_invite.affiliation_type_description, {
                  name: inviteData?.targetEntityName,
                  type: inviteData?.targetEntityAffiliationType
                    ? t(translations.affiliation_types[inviteData?.targetEntityAffiliationType])
                    : '',
                })}
          </Typography>
          <Typography variant="body1" className="accept-invite-instructions">
            {props.type === 'relationship'
              ? t(translations.accept_invite.relationship_invite_instructions, {
                  button_login: t(translations.accept_invite.login_button),
                  button_create: t(translations.accept_invite.create_account_button),
                  brand: getBrandName(appContext.state.configuration.product),
                })
              : t(translations.accept_invite.affiliation_invite_instructions, {
                  button_login: t(translations.accept_invite.login_button),
                  button_create: t(translations.accept_invite.create_account_button),
                  brand: getBrandName(appContext.state.configuration.product),
                })}
          </Typography>
          <Box className="accept-invite-create-account-button-container">
            <Button
              variant="contained"
              size="medium"
              onClick={handleNavigateToCreateAccount}
              endIcon={<PersonAddAlt1Icon />}
            >
              {t(translations.accept_invite.create_account_button)}
            </Button>
          </Box>
          <Box className="accept-invite-login-button-container">
            <Button
              variant="text"
              size="medium"
              onClick={handleNavigateToLogin}
              endIcon={<LoginIcon />}
            >
              {t(translations.accept_invite.login_button)}
            </Button>
          </Box>
        </PublicContentPage>
      )}
    </>
  );
}
